import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import toast from 'react-hot-toast';
import axios from 'axios';
import { 
  BookOpen, 
  Wand2, 
  ChevronDown,
  Loader,
  Sparkles,
  Crown,
  Lock,
  Coins,
  AlertTriangle,
  Plus
} from 'lucide-react';

const GeneratePage = () => {
  const { user, API_BASE_URL, credits, checkCreditsForOperation, refreshCredits } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    chapters: 5,
    genre: 'General',
    writing_style: '',
    themes: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [creditCheck, setCreditCheck] = useState(null);
  const navigate = useNavigate();

  const genres = [
    'General', 'Fiction', 'Non-Fiction', 'Business', 'Self-Help',
    'Romance', 'Mystery', 'Thriller', 'Science Fiction', 'Fantasy',
    'Horror', 'Biography', 'History', 'Travel', 'Cooking',
    'Technology', 'Health', 'Education', 'Children', 'Poetry'
  ];

  const writingStyles = [
    'Descriptive', 'Narrative', 'Conversational', 'Academic', 'Poetic',
    'Journalistic', 'Technical', 'Humorous', 'Formal', 'Informal'
  ];

  const themes = [
    'Love', 'Friendship', 'Betrayal', 'Redemption', 'Coming of Age',
    'Good vs Evil', 'Power', 'Identity', 'Family', 'Loss',
    'Justice', 'Freedom', 'Nature', 'Technology', 'Society',
    'War', 'Peace', 'Faith', 'Change'
  ];

  useEffect(() => {
    checkCredits();
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const checkCredits = async () => {
    // First refresh credits to get latest balance
    if (refreshCredits) {
      await refreshCredits();
    }
    
    if (checkCreditsForOperation) {
      const result = await checkCreditsForOperation('book_generation');
      setCreditCheck(result);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'chapters' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a book title');
      return;
    }

    // Check credits first
    if (creditCheck && !creditCheck.sufficient) {
      toast.error(`Insufficient credits. Need ${creditCheck.required}, have ${creditCheck.current_balance}`);
      return;
    }

    // Legacy check for non-premium users (will be removed once all users are on credit system)
    if (!user.is_premium && user.books_generated >= 3 && credits === 0) {
      toast.error('No credits available. Please purchase credits to continue generating books.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/books/generate`, formData);
      toast.success('Book generation started! Check your dashboard for progress.');
      navigate('/dashboard');
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Insufficient credits. Please purchase more credits to continue.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to start book generation');
      }
    } finally {
      setLoading(false);
    }
  };

  const canGenerate = creditCheck?.sufficient || (user.is_premium || user.books_generated < 3);

  return (
    <>
      <SEOHead 
        title="AI Book Generator - Create Complete Books with AI"
        description="Generate complete books with AI in minutes. Choose your genre, add details, and get professionally written chapters. Perfect for KDP publishing and passive income."
        keywords="AI book generator, AI writing tool, generate book with AI, book creation software, KDP book generator, automated book writing, AI manuscript generator"
        canonical="/generate"
        ogTitle="AI Book Generator - Create Complete Books in Minutes"
        ogDescription="Use AI to generate complete books with professional chapters, covers, and metadata. Perfect for Amazon KDP publishing and earning passive income."
        ogImage="/ai-book-generation.png"
        ogUrl="/generate"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "AI Book Generator",
          "description": "Generate complete books with AI for Amazon KDP publishing",
          "applicationCategory": "WritingApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Free trial with 40 credits"
          }
        }}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
        <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <Wand2 className="h-12 w-12 text-purple-400 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-white">
              Generate Your Book
            </h1>
          </div>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Create amazing books powered by artificial intelligence. Just provide the details and let our AI do the magic.
          </p>
        </div>

        {/* Credit Check Warning */}
        {creditCheck && !creditCheck.sufficient && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="h-6 w-6 text-red-400" />
                <div>
                  <h3 className="text-lg font-semibold text-red-300 mb-2">
                    Insufficient Credits
                  </h3>
                  <p className="text-red-200">
                    You need {creditCheck.required} credits to generate a book, but you only have {creditCheck.current_balance} credits.
                  </p>
                </div>
              </div>
              <Link
                to="/credits/purchase"
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-colors flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Buy Credits</span>
              </Link>
            </div>
          </div>
        )}

        {/* Credit Cost Info */}
        {creditCheck && creditCheck.sufficient && (
          <div className="bg-blue-500/20 border border-blue-500/30 rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Coins className="h-6 w-6 text-blue-400" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-300 mb-2">
                    Generation Cost
                  </h3>
                  <p className="text-blue-200">
                    This book will cost {creditCheck.required} credits. You'll have {creditCheck.remaining_after} credits remaining.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Legacy Generation Limit Warning */}
        {!user.is_premium && credits === 0 && (
          <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-yellow-300 mb-2">
                  No Credits Available
                </h3>
                <p className="text-yellow-200">
                  You need credits to generate books, covers, and optimize metadata.
                  {user.books_generated >= 3 && ' Purchase credits to continue creating.'}
                </p>
              </div>
              {user.books_generated >= 3 && (
                <Link
                  to="/credits/purchase"
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-colors flex items-center space-x-2"
                >
                  <Crown className="h-4 w-4" />
                  <span>Get Credits</span>
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Main Form */}
        <div className="bg-gray-900/50 backdrop-blur-sm rounded-3xl p-8 border border-purple-500/20">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Book Title */}
            <div>
              <label className="block text-lg font-semibold text-white mb-3">
                Book Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter your book title (e.g., 'The Ultimate Guide to Digital Marketing')"
                className="w-full px-6 py-4 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                disabled={!canGenerate}
              />
              <p className="text-gray-400 text-sm mt-2">
                This will be the main title of your book. Be specific and descriptive.
              </p>
            </div>

            {/* Number of Chapters */}
            <div>
              <label className="block text-lg font-semibold text-white mb-3">
                Number of Chapters
              </label>
              <div className="relative">
                <select
                  name="chapters"
                  value={formData.chapters}
                  onChange={handleChange}
                  className="w-full px-6 py-4 bg-gray-800 border border-gray-600 rounded-xl text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 appearance-none"
                  disabled={!canGenerate}
                >
                  {[...Array(10)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1} Chapter{i + 1 > 1 ? 's' : ''}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
              <p className="text-gray-400 text-sm mt-2">
                More chapters = longer book. Recommended: 5-10 chapters for most topics. You can always add more chapters later!
              </p>
            </div>

            {/* Genre Selection */}
            <div>
              <label className="block text-lg font-semibold text-white mb-3">
                Genre
              </label>
              <div className="relative">
                <select
                  name="genre"
                  value={formData.genre}
                  onChange={handleChange}
                  className="w-full px-6 py-4 bg-gray-800 border border-gray-600 rounded-xl text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 appearance-none"
                  disabled={!canGenerate}
                >
                  {genres.map((genre) => (
                    <option key={genre} value={genre}>
                      {genre}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
              <p className="text-gray-400 text-sm mt-2">
                Choose the genre that best fits your book's theme and style.
              </p>
            </div>

            {/* Writing Style */}
            <div>
              <label className="block text-lg font-semibold text-white mb-3">
                Writing Style (Optional)
              </label>
              <div className="relative">
                <select
                  name="writing_style"
                  value={formData.writing_style}
                  onChange={handleChange}
                  className="w-full px-6 py-4 bg-gray-800 border border-gray-600 rounded-xl text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 appearance-none"
                  disabled={!canGenerate}
                >
                  <option value="">Select writing style...</option>
                  {writingStyles.map((style) => (
                    <option key={style} value={style}>
                      {style}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
              <p className="text-gray-400 text-sm mt-2">
                Choose a writing style to influence the tone and voice of your book content.
              </p>
            </div>

            {/* Themes */}
            <div>
              <label className="block text-lg font-semibold text-white mb-3">
                Themes (Optional)
              </label>
              <div className="relative">
                <select
                  name="themes"
                  value={formData.themes}
                  onChange={handleChange}
                  className="w-full px-6 py-4 bg-gray-800 border border-gray-600 rounded-xl text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 appearance-none"
                  disabled={!canGenerate}
                >
                  <option value="">Select theme...</option>
                  {themes.map((theme) => (
                    <option key={theme} value={theme}>
                      {theme}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
              <p className="text-gray-400 text-sm mt-2">
                Add thematic elements to guide the content direction and depth.
              </p>
            </div>

            {/* Book Description */}
            <div>
              <label className="block text-lg font-semibold text-white mb-3">
                Book Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="Provide additional context, target audience, specific topics to cover, or any other details that will help the AI create better content..."
                className="w-full px-6 py-4 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 resize-none"
                disabled={!canGenerate}
              />
              <p className="text-gray-400 text-sm mt-2">
                Additional details help our AI create more targeted and relevant content.
              </p>
            </div>

            {/* Premium Features Preview */}
            {!user.is_premium && (
              <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-2xl p-6">
                <div className="flex items-center mb-4">
                  <Crown className="h-6 w-6 text-yellow-400 mr-2" />
                  <h3 className="text-lg font-semibold text-white">Premium Features</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-300">
                  <div className="flex items-center">
                    <Sparkles className="h-4 w-4 text-purple-400 mr-2" />
                    Unlimited book generations
                  </div>
                  <div className="flex items-center">
                    <Sparkles className="h-4 w-4 text-purple-400 mr-2" />
                    Advanced AI models
                  </div>
                  <div className="flex items-center">
                    <Sparkles className="h-4 w-4 text-purple-400 mr-2" />
                    Multiple export formats
                  </div>
                  <div className="flex items-center">
                    <Sparkles className="h-4 w-4 text-purple-400 mr-2" />
                    Priority support
                  </div>
                </div>
              </div>
            )}

            {/* Generate Button */}
            <div className="flex justify-center pt-6">
              {canGenerate ? (
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-12 py-4 rounded-2xl font-bold text-lg hover:from-purple-600 hover:to-pink-600 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-3"
                >
                  {loading ? (
                    <>
                      <Loader className="h-6 w-6 animate-spin" />
                      <span>Generating Your Book...</span>
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-6 w-6" />
                      <span>Generate Book with AI</span>
                    </>
                  )}
                </button>
              ) : (
                <div className="text-center">
                  <div className="bg-gray-700 text-gray-400 px-12 py-4 rounded-2xl font-bold text-lg flex items-center space-x-3 mb-4">
                    <Lock className="h-6 w-6" />
                    <span>Free Limit Reached</span>
                  </div>
                  <p className="text-gray-400 mb-4">
                    Upgrade to premium to continue generating books
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
                  >
                    Upgrade to Premium
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* How It Works */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            How AI Book Generation Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">1</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Analyze Your Input</h3>
              <p className="text-gray-300">
                Our AI analyzes your title, genre, and description to understand your vision.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">2</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Create Structure</h3>
              <p className="text-gray-300">
                Generate a detailed outline and structure for each chapter of your book.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-lg">3</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Write Content</h3>
              <p className="text-gray-300">
                Generate engaging, well-written content for each chapter with proper flow.
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default GeneratePage;